import { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Layers, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import type { CompositeComponent } from './CompositeComponentEditor';

// A variant row in the matrix
export interface VariantRecipeRow {
  variantName: string;
  variantIndex: number;
  price: string; // string for input field
  componentQuantities: { [componentIndex: number]: string }; // string for input field
}

interface CompositeVariantMatrixProps {
  // The base recipe components (defines which components exist)
  baseComponents: CompositeComponent[];
  // Variant names generated from the attribute config (e.g., "Small", "Medium", "Large")
  variantNames: string[];
  // Current variant recipe data
  variantRecipes: VariantRecipeRow[];
  // Called when any cell changes
  onChange: (recipes: VariantRecipeRow[]) => void;
  // Base product price (used as default for variant prices)
  basePrice: string;
  disabled?: boolean;
}

function QuantityInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-8 w-20 text-center tabular-nums text-sm"
    />
  );
}

export function CompositeVariantMatrix({
  baseComponents,
  variantNames,
  variantRecipes,
  onChange,
  basePrice,
  disabled = false,
}: CompositeVariantMatrixProps) {
  const { formatCurrency } = useCurrency();

  // Initialize recipes when variant names or base components change
  useEffect(() => {
    if (variantNames.length === 0 || baseComponents.length === 0) {
      if (variantRecipes.length > 0) onChange([]);
      return;
    }

    // Only initialize if the variant count changed
    if (variantRecipes.length === variantNames.length) {
      // Update names if they changed
      const needsNameUpdate = variantRecipes.some((r, i) => r.variantName !== variantNames[i]);
      if (needsNameUpdate) {
        onChange(variantRecipes.map((r, i) => ({ ...r, variantName: variantNames[i] })));
      }
      return;
    }

    // Build new recipe rows pre-filled from base component quantities
    const newRecipes: VariantRecipeRow[] = variantNames.map((name, variantIdx) => {
      // Check if there's an existing recipe for this name
      const existing = variantRecipes.find(r => r.variantName === name);
      if (existing) return { ...existing, variantIndex: variantIdx };

      const componentQtys: { [key: number]: string } = {};
      baseComponents.forEach((comp, compIdx) => {
        componentQtys[compIdx] = comp.quantity.toString();
      });

      return {
        variantName: name,
        variantIndex: variantIdx,
        price: basePrice || '',
        componentQuantities: componentQtys,
      };
    });

    onChange(newRecipes);
  }, [variantNames.length, baseComponents.length]);

  if (variantNames.length === 0 || baseComponents.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-6">
        Define the base recipe and variant attributes above to configure per-variant quantities.
      </div>
    );
  }

  const handlePriceChange = (variantIdx: number, value: string) => {
    const updated = [...variantRecipes];
    if (updated[variantIdx]) {
      updated[variantIdx] = { ...updated[variantIdx], price: value };
      onChange(updated);
    }
  };

  const handleQuantityChange = (variantIdx: number, compIdx: number, value: string) => {
    const updated = [...variantRecipes];
    if (updated[variantIdx]) {
      updated[variantIdx] = {
        ...updated[variantIdx],
        componentQuantities: {
          ...updated[variantIdx].componentQuantities,
          [compIdx]: value,
        },
      };
      onChange(updated);
    }
  };

  // Calculate component cost for a variant row
  const calcVariantCost = (recipe: VariantRecipeRow): number => {
    let total = 0;
    baseComponents.forEach((comp, compIdx) => {
      const qty = parseFloat(recipe.componentQuantities[compIdx] || '0');
      if (isNaN(qty)) return;
      let unitCost = 0;
      if (comp.componentProduct) {
        unitCost = comp.componentProduct.cost || comp.componentProduct.price || 0;
      } else if (comp.rawItem) {
        unitCost = comp.rawItem.cost_per_unit || 0;
      }
      total += qty * unitCost;
    });
    return total;
  };

  // Get component display name
  const getCompName = (comp: CompositeComponent): string => {
    if (comp.componentProduct) return comp.componentProduct.name;
    if (comp.rawItem) return comp.rawItem.name;
    return 'Unknown';
  };

  const isRawComp = (comp: CompositeComponent): boolean => {
    return !!comp.rawItem || !!comp.rawItemId;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Variant Recipe Matrix</h4>
        <Badge variant="secondary" className="text-xs">
          {variantNames.length} variant{variantNames.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      <p className="text-xs text-muted-foreground">
        Adjust the quantity of each component per variant. Prices are set independently for each variant.
      </p>

      <div className="rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">
                Variant
              </th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground whitespace-nowrap w-24">
                Price
              </th>
              {baseComponents.map((comp, idx) => (
                <th key={idx} className="text-center px-3 py-2 font-medium text-muted-foreground whitespace-nowrap">
                  <div className="flex items-center justify-center gap-1">
                    {isRawComp(comp) ? (
                      <Layers className="h-3 w-3 text-amber-500" />
                    ) : (
                      <Package className="h-3 w-3 text-muted-foreground" />
                    )}
                    <span className="max-w-[100px] truncate">{getCompName(comp)}</span>
                  </div>
                </th>
              ))}
              <th className="text-right px-3 py-2 font-medium text-muted-foreground whitespace-nowrap w-24">
                Cost
              </th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground whitespace-nowrap w-20">
                Margin
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Base recipe row (read-only reference) */}
            <tr className="border-b bg-muted/10">
              <td className="px-3 py-2">
                <span className="text-xs text-muted-foreground italic">Base recipe</span>
              </td>
              <td className="px-3 py-2 text-right">
                <span className="text-xs text-muted-foreground">
                  {basePrice ? formatCurrency(parseFloat(basePrice)) : '--'}
                </span>
              </td>
              {baseComponents.map((comp, idx) => (
                <td key={idx} className="px-3 py-2 text-center">
                  <span className="text-xs text-muted-foreground tabular-nums">{comp.quantity}</span>
                </td>
              ))}
              <td className="px-3 py-2 text-right">
                <span className="text-xs text-muted-foreground">--</span>
              </td>
              <td className="px-3 py-2 text-right">
                <span className="text-xs text-muted-foreground">--</span>
              </td>
            </tr>

            {/* Variant rows */}
            {variantRecipes.map((recipe, variantIdx) => {
              const cost = calcVariantCost(recipe);
              const price = parseFloat(recipe.price) || 0;
              const margin = price > 0 ? ((price - cost) / price) * 100 : 0;

              return (
                <tr key={variantIdx} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-3 py-2">
                    <span className="font-medium text-sm">{recipe.variantName}</span>
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={recipe.price}
                      onChange={(e) => handlePriceChange(variantIdx, e.target.value)}
                      disabled={disabled}
                      className="h-8 w-24 text-right tabular-nums text-sm"
                      placeholder="0.00"
                    />
                  </td>
                  {baseComponents.map((_comp, compIdx) => (
                    <td key={compIdx} className="px-3 py-2 text-center">
                      <QuantityInput
                        value={recipe.componentQuantities[compIdx] || '0'}
                        onChange={(val) => handleQuantityChange(variantIdx, compIdx, val)}
                        disabled={disabled}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <span className="text-sm tabular-nums font-medium">
                      {formatCurrency(cost)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={cn(
                      "text-sm tabular-nums",
                      margin < 0 && "text-red-600",
                      margin >= 0 && margin < 30 && "text-yellow-600",
                      margin >= 30 && "text-green-600"
                    )}>
                      {price > 0 ? `${margin.toFixed(0)}%` : '--'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      {variantRecipes.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span>
            {variantRecipes.length} variant{variantRecipes.length !== 1 ? 's' : ''} configured
          </span>
          <span>
            Price range: {(() => {
              const prices = variantRecipes
                .map(r => parseFloat(r.price))
                .filter(p => !isNaN(p) && p > 0);
              if (prices.length === 0) return '--';
              const min = Math.min(...prices);
              const max = Math.max(...prices);
              return min === max ? formatCurrency(min) : `${formatCurrency(min)} - ${formatCurrency(max)}`;
            })()}
          </span>
        </div>
      )}
    </div>
  );
}
